import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_NODE_URL,
  BULK_PRODUCT_UPDATE_SUBMIT,
  BULK_PRODUCT_UPDATE_JOBS,
  BULK_PRODUCT_UPDATE_JOB_DETAIL,
  BULK_PRODUCT_UPDATE_CANCEL,
} from "../../../Constants/Config";
import { getAuthDetails } from "../../../Common/cookiesHelper";

const initialState = {
  submitLoading: false,
  submitError: "",
  lastJobId: null,
  jobs: [],
  jobsCount: 0,
  jobsLoading: false,
  jobDetail: null,
  jobDetailLoading: false,
};

const nodePost = async (endpoint, data, rejectWithValue) => {
  // getAuthDetails is the plain (non-hook) cookie reader — safe outside React.
  const { userTypeData } = getAuthDetails();
  const { token, ...otherUserData } = userTypeData;
  try {
    const response = await axios.post(
      BASE_NODE_URL + endpoint,
      { ...data, ...otherUserData },
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    return rejectWithValue({
      message:
        error.response?.data?.message || error.message || "Request failed",
      status: toThunkRejectValue(error).status,
      data: error.response ? error.response.data : null,
    });
  }
};

export const submitBulkUpdate = createAsyncThunk(
  "bulkProductUpdate/submit",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_PRODUCT_UPDATE_SUBMIT, data, rejectWithValue)
);

export const fetchBulkJobs = createAsyncThunk(
  "bulkProductUpdate/jobs",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_PRODUCT_UPDATE_JOBS, data, rejectWithValue)
);

export const fetchBulkJobDetail = createAsyncThunk(
  "bulkProductUpdate/jobDetail",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_PRODUCT_UPDATE_JOB_DETAIL, data, rejectWithValue)
);

export const cancelBulkJob = createAsyncThunk(
  "bulkProductUpdate/cancel",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_PRODUCT_UPDATE_CANCEL, data, rejectWithValue)
);

const bulkProductUpdateSlice = createSlice({
  name: "bulkProductUpdate",
  initialState,
  reducers: {
    clearJobDetail(state) {
      state.jobDetail = null;
    },
    clearSubmitState(state) {
      state.submitLoading = false;
      state.submitError = "";
      state.lastJobId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitBulkUpdate.pending, (state) => {
        state.submitLoading = true;
        state.submitError = "";
        state.lastJobId = null;
      })
      .addCase(submitBulkUpdate.fulfilled, (state, action) => {
        state.submitLoading = false;
        if (action.payload?.status) {
          state.lastJobId = action.payload.job_id;
        } else {
          state.submitError = action.payload?.message || "Submit failed";
        }
      })
      .addCase(submitBulkUpdate.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload?.message || "Submit failed";
      })
      .addCase(fetchBulkJobs.pending, (state) => {
        state.jobsLoading = true;
      })
      .addCase(fetchBulkJobs.fulfilled, (state, action) => {
        state.jobsLoading = false;
        if (action.payload?.status) {
          state.jobs = action.payload.result || [];
          state.jobsCount = Number(action.payload.total_count) || 0;
        }
      })
      .addCase(fetchBulkJobs.rejected, (state) => {
        state.jobsLoading = false;
      })
      .addCase(fetchBulkJobDetail.pending, (state) => {
        state.jobDetailLoading = true;
      })
      .addCase(fetchBulkJobDetail.fulfilled, (state, action) => {
        state.jobDetailLoading = false;
        if (action.payload?.status) {
          state.jobDetail = {
            job: action.payload.job,
            stores: action.payload.stores || [],
            products: action.payload.products || [],
          };
        }
      })
      .addCase(fetchBulkJobDetail.rejected, (state) => {
        state.jobDetailLoading = false;
      });
  },
});

export const { clearJobDetail, clearSubmitState } =
  bulkProductUpdateSlice.actions;
export default bulkProductUpdateSlice.reducer;
